/**
 * Formatos del portal que no son montos (esos van por `useMoney` y `<Monto>`).
 *
 * ⚠️ Las fechas llegan como `aaaa-mm-dd` y se formatean **cortando el texto**, no
 * con `new Date(...)`: una fecha sin hora se interpreta en UTC, y en Chile
 * (UTC-4) el 01-10 se mostraría como 30-09.
 */
const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

/** `2026-09-28` → `28-09-2026`. Cualquier otra cosa se devuelve tal cual. */
export function fechaCorta(iso: string | null | undefined): string {
  if (!iso) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso
}

/** `(2026, 9)` → `septiembre 2026`. */
export function mesYAnio(anio: number, mes: number): string {
  return `${MESES[mes - 1] ?? mes} ${anio}`
}

/** Días con decimales a la chilena y sin ceros de más: `12.50` → `12,5`, `3.00` → `3`. */
export function dias(valor: string | number | null | undefined): string {
  const n = Number(valor ?? 0)
  if (!Number.isFinite(n)) return '0'
  return n.toLocaleString('es-CL', { maximumFractionDigits: 2 })
}

/**
 * Antigüedad por calendario desde la fecha de ingreso: `2024-05-14` al
 * `2026-09-30` → `2 años 4 meses`. Se cuenta por aniversario y no dividiendo días
 * por 365,25, que da meses aproximados.
 */
export function antiguedadDesde(ingreso: string | null | undefined, hoy: Date = new Date()): string {
  const m = ingreso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(ingreso) : null
  if (!m) return '—'
  let meses = (hoy.getFullYear() - Number(m[1])) * 12 + (hoy.getMonth() + 1 - Number(m[2]))
  if (hoy.getDate() < Number(m[3])) meses -= 1
  if (meses < 0) return '—'
  const a = Math.floor(meses / 12)
  const r = meses % 12
  const partes: string[] = []
  if (a) partes.push(`${a} ${a === 1 ? 'año' : 'años'}`)
  if (r) partes.push(`${r} ${r === 1 ? 'mes' : 'meses'}`)
  return partes.join(' ') || 'menos de un mes'
}

/** Un valor en UF, con coma y sin ceros de más: `5.8960` → `5,896 UF`. */
export function uf(valor: string | number | null | undefined): string {
  const n = Number(valor)
  if (!Number.isFinite(n) || n <= 0) return '—'
  return `${n.toLocaleString('es-CL', { maximumFractionDigits: 4 })} UF`
}

/** Un valor opcional: el guion cuando falta, en vez de un hueco. */
export function o<T>(v: T | null | undefined | ''): T | '—' {
  return v === null || v === undefined || v === '' ? '—' : v
}
